import React from 'react';
import { useTranslation } from 'react-i18next';

import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@core-ui/Components/ui/dialog';
import { Button } from '@core-ui/Components/ui/button';

import { AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';

import type { ContactInfoDto } from '@features/contacts';
import { statusFromDto } from '@features/contacts';

interface DeleteContactDialogProps {
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  contact: ContactInfoDto | null;
  isLoading: boolean;
  onConfirmDelete: () => void;
}

function ContactStatusIcon ({ status }: { status: 'warning' | 'ok' | 'neutral' }): React.JSX.Element {
  if (status === 'warning') {
    return <AlertTriangle className="h-4 w-4 text-amber-500" />;
  }
  return <CheckCircle2 className="h-4 w-4 text-emerald-600" />;
}

export default function DeleteContactDialog ({
  isOpen,
  onOpenChange,
  contact,
  isLoading,
  onConfirmDelete,
}: DeleteContactDialogProps): React.JSX.Element {
  const { t } = useTranslation();

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[520px] bg-card border-border text-card-foreground rounded-md p-4 md:p-6 shadow">
        <DialogHeader>
          <DialogTitle>{t('contacts.deleteContactDialog.title')}</DialogTitle>
          <DialogDescription>
            {t('contacts.deleteContactDialog.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="rounded-md border border-border p-3">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="truncate font-medium">{contact?.name ?? '—'}</div>
              <div className="mt-1 text-xs text-muted-foreground">
                {contact?.accountName ?? '—'} • {contact?.jobTitle ?? '—'}
              </div>
            </div>
            {contact !== null && <ContactStatusIcon status={statusFromDto(contact)} />}
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            className="border border-border text-muted-foreground hover:bg-muted"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t('contacts.deleteContactDialog.cancel')}
          </Button>
          <Button
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={isLoading}
            className="gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('contacts.deleteContactDialog.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
